"use client";

import { useLocale, useTranslations } from "next-intl";
import { useState } from "react";
import { toast } from "sonner";
import { useAuthDialogStore } from "@/features/user/ui/store";
import { AnalyticsEvents, trackEvent } from "@/lib/client/analytics/product-analytics";
import { authClient } from "@/lib/client/auth/client";
import {
  Button,
  cn,
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  HStack,
  Separator,
  Text,
  VStack,
} from "@/ui";
import { GoogleIcon } from "./icons";
import { SignInForm } from "./SignInForm";
import { SignUpForm } from "./SignUpForm";

/**
 * SignInDialog component - Authentication dialog
 */
export const SignInDialog = () => {
  const locale = useLocale();
  const toastT = useTranslations("toast");
  const usersT = useTranslations("users");
  const { isAuthDialogOpen, toggleAuthDialog } = useAuthDialogStore();
  const [isGoogleLoading, setIsGoogleLoading] = useState(false);
  const [isSignUp, setIsSignUp] = useState(true);

  const handleGoogleSignIn = async () => {
    trackEvent(AnalyticsEvents.AUTH_GOOGLE_SIGN_IN_CLICKED);
    setIsGoogleLoading(true);
    try {
      await authClient.signIn.social({
        provider: "google",
        callbackURL: `/${locale}`,
      });
    } catch (error) {
      toast.error(error instanceof Error ? error.message : toastT("error.message"));
    } finally {
      setIsGoogleLoading(false);
    }
  };

  const handleSignUpToggle = () => {
    trackEvent(AnalyticsEvents.AUTH_SIGN_UP_STARTED, { method: "email" });
    setIsSignUp(true);
  };

  const handleSignInToggle = () => {
    trackEvent(AnalyticsEvents.AUTH_SIGN_IN_STARTED, { method: "email" });
    setIsSignUp(false);
  };

  return (
    <Dialog
      open={isAuthDialogOpen}
      onOpenChange={() => toggleAuthDialog(false)}
    >
      <DialogContent
        className="flex  flex-col overflow-y-auto px-6 pb-6 pt-5 sm:h-[450px] sm:max-w-[460px] sm:overflow-hidden sm:px-8 sm:pb-8 sm:pt-6"
        data-testid="auth-dialog"
      >
        <VStack className="relative h-full w-full gap-3">
          <DialogHeader>
            <DialogTitle>
              <HStack className="items-center justify-center gap-4">
                <Text
                  onClick={handleSignUpToggle}
                  className={cn(
                    isSignUp ? "text-4xl font-bold" : "link text-sm",
                    "pb-4 text-center transition-all duration-300 ease-in-out cursor-pointer",
                  )}
                >
                  {usersT("sign_up")}
                </Text>
                <Text
                  onClick={handleSignInToggle}
                  className={cn(
                    isSignUp ? "link text-sm" : "text-4xl font-bold",
                    "pb-4 text-center transition-all duration-300 ease-in-out cursor-pointer",
                  )}
                >
                  {usersT("sign_in")}
                </Text>
              </HStack>
            </DialogTitle>
          </DialogHeader>
          <VStack className="w-full flex-1 justify-start">
            {isSignUp ? <SignUpForm /> : <SignInForm />}
          </VStack>
          <Separator className="my-6 w-full">
            <Text className="w-full text-center text-sm text-muted-foreground">
              {usersT("or_login")}
            </Text>
          </Separator>

          <VStack className="w-full items-center space-y-2 pb-4">
            <Button
              variant="outline"
              autoFocus={true}
              onClick={handleGoogleSignIn}
              disabled={isGoogleLoading}
              className={isGoogleLoading ? "loading" : "w-full flex-1"}
            >
              <GoogleIcon className="h-5 w-5" />
              Google
            </Button>
          </VStack>
        </VStack>
      </DialogContent>
    </Dialog>
  );
};
